/**
 * Errors from a tab running an older build than the server (or the other way round, mid-rollout):
 * the browser asks for a code chunk that this build doesn't have.
 */
const STALE_BUILD_ERRORS = [
  /ChunkLoadError/,
  /Loading (CSS )?chunk [\w-]+ failed/,
  /Failed to fetch dynamically imported module/,
  /Importing a module script failed/,
  /error loading dynamically imported module/,
  // Webpack finding no module for an id: Chrome and Edge, then Safari.
  /\(reading 'call'\)/,
  /\(evaluating '[\w$]+\[[\w$]+\]\.call'\)/,
];

const RELOAD_KEY = 'tlc:stale-build-reload';

/** Don't reload again this soon, so a real bug can't reload in a loop. */
const RELOAD_GAP_MS = 30_000;

export function isStaleBuildError(error: Error) {
  const text = `${error.name}: ${error.message}`;
  return STALE_BUILD_ERRORS.some((pattern) => pattern.test(text));
}

type Storage = Pick<globalThis.Storage, 'getItem' | 'setItem'>;

function sessionStore(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/** True when this stale-build error should reload the page: no reload for one in the last 30 seconds. */
export function shouldReloadForNewBuild(error: Error, storage: Storage | null = sessionStore(), now = Date.now()) {
  if (!isStaleBuildError(error)) return false;
  try {
    const last = Number(storage?.getItem(RELOAD_KEY) || 0);
    return now - last > RELOAD_GAP_MS;
  } catch {
    return true;
  }
}

/** Loads the page fresh from the server, which gets the new build's code. */
export function reloadForNewBuild(storage: Storage | null = sessionStore(), now = Date.now()) {
  try {
    storage?.setItem(RELOAD_KEY, String(now));
  } catch {
    // reload anyway
  }
  window.location.reload();
}
